import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Input, message, Spin } from "antd";
import moment from "moment";
import axios from "axios";
import debounce from "lodash/debounce";
import Select from "react-select";
import {
  ShoppingCart,
  User,
  Phone,
  RefreshCw,
  RotateCcw,
  AlertCircle,
  CheckCircle,
  CreditCard,
} from "react-feather";

import CategoryTabs   from "./components/billing/CategoryTabs";
import SearchBar      from "./components/billing/SearchBar";
import MenuItemGrid   from "./components/billing/MenuItemGrid";
import BillItemList   from "./components/billing/BillItemList";
import BillSummary    from "./components/billing/BillSummary";
// PrintBill import removed — printing handled by kitchen agent via Socket.IO
import { useNotification } from "../../context/NotificationContext";

const BUSINESS_DATE_KEYS = ["posbusiness_date", "business_date", "pos_business_date"];
const staticRateCardSources = ["Dine-In", "Dine In", "Room Service", "Take Away", "Takeaway"];
const TOP_NAV_HEIGHT = 60;

const toNumber = (value, fallback = 0) => {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : fallback;
};

const getCurrency = () => localStorage.getItem("currencycode") || "₹";

const money = (value) => {
  const n = toNumber(value);
  return n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const getBusinessDate = () => {
  for (const key of BUSINESS_DATE_KEYS) {
    const value = localStorage.getItem(key);
    if (value && moment(value).isValid()) return moment(value).format("YYYY-MM-DD");
  }
  return moment().format("YYYY-MM-DD");
};

const normalizeArrayResponse = (data, key) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.[key])) return data[key];
  if (Array.isArray(data?.data)) return data.data;
  return [];
};

const counterSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 40, height: 40, borderRadius: 14,
    borderColor: state.isFocused ? "#2563eb" : "rgba(15, 23, 42, 0.10)",
    boxShadow: state.isFocused ? "0 0 0 3px rgba(37, 99, 235, 0.12)" : "none",
    background: "#ffffff", fontSize: 13, fontWeight: 850, cursor: "pointer",
    "&:hover": { borderColor: state.isFocused ? "#2563eb" : "rgba(15, 23, 42, 0.20)" },
  }),
  valueContainer: (base) => ({ ...base, padding: "0 10px", height: 40 }),
  indicatorsContainer: (base) => ({ ...base, height: 40 }),
  dropdownIndicator: (base) => ({ ...base, padding: 6 }),
  menuPortal: (base) => ({ ...base, zIndex: 2147483647 }),
  menu: (base) => ({ ...base, zIndex: 2147483647, borderRadius: 14, overflow: "hidden", boxShadow: "0 18px 42px rgba(15, 23, 42, 0.18)" }),
  option: (base, state) => ({ ...base, fontSize: 13, fontWeight: state.isSelected ? 900 : 750, cursor: "pointer" }),
  singleValue: (base) => ({ ...base, color: "#0f172a", fontWeight: 900 }),
};

const normalizeSettings = (settingsData = {}) => {
  const normalized = { ...settingsData };
  normalized.delivery_settings = normalized.delivery_settings || {
    enable_delivery_charges: 0, free_above_order_value: 0,
    rate_card_specific_charges: [], distance_based_charges: [],
    include_delivery_in_bill_total: false, calculation_mode: "flat",
  };
  normalized.billing_settings = normalized.billing_settings || {
    enable_service_charges: 0, service_charge_percentage: 0,
    round_off_total_amount: null, bill_no_formatting: null,
  };
  normalized.payment_settings = normalized.payment_settings || {};
  normalized.rate_card_sources = Array.isArray(normalized.rate_card_sources)
    ? normalized.rate_card_sources.map((src) => (typeof src === "string" ? { source: src } : src)).filter((src) => src?.source)
    : [];
  normalized.payment_types = Array.isArray(normalized.payment_types)
    ? normalized.payment_types.map((pt) => (typeof pt === "string" ? { type: pt } : pt)).filter((pt) => pt?.type)
    : [];
  return normalized;
};

const CounterBillingPage = () => {
  const [selectedCategory, setSelectedCategory] = useState("All Products");
  const [billItems, setBillItems] = useState([]);
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [paymentMode, setPaymentMode] = useState(null);
  const [taxIncluded, setTaxIncluded] = useState(true);
  const [orderSource, setOrderSource] = useState({ value: "Dine-In", label: "Dine-In" });
  const [rateCard, setRateCard] = useState(null);
  const [discountPercentage, setDiscountPercentage] = useState(0);
  const [isFooterExpanded, setIsFooterExpanded] = useState(false);
  const [isPartialPaymentEnabled, setIsPartialPaymentEnabled] = useState(false);
  const [partialPayments, setPartialPayments] = useState([]);
  const [currentPartialMethod, setCurrentPartialMethod] = useState("CASH");
  const [currentPartialAmount, setCurrentPartialAmount] = useState("");
  const [isComplementary, setIsComplementary] = useState(false);
  const [categories, setCategories] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [bootLoading, setBootLoading] = useState(true);
  const [error, setError] = useState(null);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [settings, setSettings] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const { notify } = useNotification();

  // printIframeRef removed — printing handled by kitchen agent via Socket.IO
  const apiUrl  = process.env.REACT_APP_API_URL || "http://localhost:3000/api/pos";
  const token   = localStorage.getItem("token");  // inside component — reads current value on each render
  const currency = getCurrency();

  const paymentOptions = useMemo(() => {
    if (settings?.payment_types?.length) {
      return settings.payment_types.map((pt) => ({ value: pt.type, label: pt.type }));
    }
    return [
      { value: "CASH",        label: "CASH"        },
      { value: "UPI",         label: "UPI"         },
      { value: "Credit CARD", label: "Credit CARD" },
      { value: "Due",         label: "Due"         },
    ];
  }, [settings]);

  const sourceOptions = useMemo(() => {
    const staticOptions  = staticRateCardSources.map((s) => ({ value: s, label: s }));
    const dynamicOptions = settings?.rate_card_sources?.length
      ? settings.rate_card_sources.map((src) => ({ value: src.source, label: src.source }))
      : [];
    const seen = new Set();
    return [...staticOptions, ...dynamicOptions].filter((opt) => {
      const key = String(opt.value).toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [settings]);

  const getDefaultPaymentOption = useCallback(
    (nextSettings = settings) => {
      const defaultMode = nextSettings?.payment_settings?.default_payment_mode;
      if (!defaultMode) return null;
      return (
        paymentOptions.find((opt) => String(opt.value).toLowerCase() === String(defaultMode).toLowerCase()) ||
        { value: defaultMode, label: defaultMode }
      );
    },
    [paymentOptions, settings]
  );

  // Boot: fire all three requests in parallel
  useEffect(() => {
    let isMounted = true;
    const headers = { Authorization: `Bearer ${token}` };

    const settingsPromise = axios.get(`${apiUrl}/setting-pos`, { headers })
      .then((res) => {
        if (!isMounted) return;
        const normalized = normalizeSettings(res.data.settings || {});
        setSettings(normalized);
        const defaultMode = normalized.payment_settings?.default_payment_mode;
        if (defaultMode) {
          const option =
            normalized.payment_types?.map((pt) => ({ value: pt.type, label: pt.type }))
              .find((pt) => String(pt.value).toLowerCase() === String(defaultMode).toLowerCase()) ||
            { value: defaultMode, label: defaultMode };
          setPaymentMode(option);
          setCurrentPartialMethod(option.value);
        }
      })
      .catch((err) => {
        if (isMounted) notify("error",err.response?.data?.error || "Failed to fetch POS settings");
      });

    const categoriesPromise = axios.get(`${apiUrl}/pos/categories/listings`, { headers })
      .then((res) => {
        if (!isMounted) return;
        setCategories(
          normalizeArrayResponse(res.data, "categories")
            .filter((cat) => cat?.is_active && cat.category_id)
        );
      })
      .catch((err) => {
        if (isMounted) notify("error",err.response?.data?.error || "Failed to fetch categories");
      });

    const itemsPromise = axios.get(`${apiUrl}/pos/items/listings`, { headers })
      .then((res) => {
        if (!isMounted) return;
        setMenuItems(
          normalizeArrayResponse(res.data, "items")
            .filter((item) => item?.is_active && item.menu_id && item.name)
            .map((item) => ({
              ...item,
              price_standard: toNumber(item.price_standard),
              total_tax_rate: toNumber(item.total_tax_rate),
              tax_breakdown:  Array.isArray(item.tax_breakdown) ? item.tax_breakdown : [],
              unit: item.unit || "NOS",
            }))
        );
      })
      .catch((err) => {
        if (isMounted) setError(err.response?.data?.error || "Failed to fetch menu items");
      });

    Promise.allSettled([settingsPromise, categoriesPromise, itemsPromise]).then(() => {
      if (isMounted) setBootLoading(false);
    });

    return () => { isMounted = false; };
  }, [apiUrl, token]);

  const displayedItems = useMemo(() => {
    if (selectedCategory === "All Products") return menuItems;
    return menuItems.filter((item) => item.category_name === selectedCategory);
  }, [selectedCategory, menuItems]);

  const filteredItems = useMemo(() => {
    if (!searchTerm) return displayedItems;
    const searchLower = searchTerm.toLowerCase();
    return displayedItems.filter((item) =>
      [item.name, item.category_name, item.item_code, item.code]
        .filter(Boolean).join(" ").toLowerCase().includes(searchLower)
    );
  }, [searchTerm, displayedItems]);

  const debouncedSetSearchTerm = useMemo(
    () => debounce((value) => setSearchTerm(value), 250),
    []
  );

  useEffect(() => {
    return () => { debouncedSetSearchTerm.cancel?.(); };
  }, [debouncedSetSearchTerm]);

  const getEffectivePrice = useCallback(
    (item) => {
      let price = toNumber(item.price_standard || item.price || item.rate);
      if (!staticRateCardSources.includes(orderSource.value) && Array.isArray(item.source_prices)) {
        const sourcePrice = item.source_prices.find((sp) => sp.source === orderSource.value);
        if (sourcePrice) price = toNumber(sourcePrice.price, price);
      }
      if (rateCard?.value === "happy") price *= 0.9;
      if (rateCard?.value === "promo") price *= 0.8;
      return price;
    },
    [orderSource.value, rateCard]
  );

  const handleAddItem = useCallback(
    (item) => {
      const menuId = item?.menu_id || item?.item_id || item?.id;
      if (!menuId) { notify("error","Unable to add item. Menu item id is missing."); return; }
      const price = getEffectivePrice(item);
      setBillItems((prev) => {
        const existing = prev.find((i) => String(i.menu_id) === String(menuId));
        if (existing) {
          return prev.map((i) =>
            String(i.menu_id) === String(menuId)
              ? { ...i, qty: toNumber(i.qty) + 1, amount: (toNumber(i.qty) + 1) * price, price }
              : i
          );
        }
        return [
          ...prev,
          {
            ...item, menu_id: menuId, qty: 1, amount: price, price,
            tax_name: item.tax_name, tax_rate: toNumber(item.total_tax_rate),
            tax_breakdown: Array.isArray(item.tax_breakdown) ? item.tax_breakdown : [],
            is_price_exclusive_tax: item.is_price_exclusive_tax,
          },
        ];
      });
    },
    [getEffectivePrice]
  );

  const handleQuantityChange = useCallback(
    (menuId, change) => {
      setBillItems((prev) =>
        prev.map((item) => {
          if (String(item.menu_id) !== String(menuId)) return item;
          const newQty = Math.max(1, toNumber(item.qty) + toNumber(change));
          const price  = getEffectivePrice(item);
          return { ...item, qty: newQty, amount: newQty * price, price };
        })
      );
    },
    [getEffectivePrice]
  );

  const handleRemoveItem = useCallback((menuId) => {
    setBillItems((prev) => prev.filter((item) => String(item.menu_id) !== String(menuId)));
  }, []);

  const {
    subtotal, taxTotal, taxBreakup, deliveryCharge, serviceCharge,
    grandTotal, unroundedTotal, discountAmount,
  } = useMemo(() => {
    const sub = billItems.reduce((sum, item) => sum + toNumber(item.amount), 0);
    const taxMap = {};
    let additionalTax = 0;

    billItems.forEach((item) => {
      if (!item.is_taxable) return;
      const breakdown =
        Array.isArray(item.tax_breakdown) && item.tax_breakdown.length
          ? item.tax_breakdown
          : [{ tax_id: "unknown", name: item.tax_name || "Tax", rate: item.total_tax_rate || item.tax_rate || 0 }];

      breakdown.forEach((tax) => {
        const taxRate = toNumber(tax.rate ?? tax.tax_rate);
        let taxAmount = 0;
        if (item.is_price_exclusive_tax) {
          taxAmount = (toNumber(item.amount) * taxRate) / 100;
          additionalTax += taxAmount;
        } else {
          const totalTaxRate = toNumber(item.total_tax_rate || item.tax_rate);
          if (totalTaxRate > 0) {
            const baseAmount     = toNumber(item.amount) / (1 + totalTaxRate / 100);
            const totalTaxAmount = toNumber(item.amount) - baseAmount;
            taxAmount = totalTaxAmount * (taxRate / totalTaxRate);
          }
        }
        const taxId = tax.tax_id || tax.id || tax.name || "unknown";
        if (!taxMap[taxId]) {
          taxMap[taxId] = { tax_id: taxId, tax_name: tax.name || tax.tax_name || "Tax", tax_rate: taxRate, amount: 0 };
        }
        taxMap[taxId].amount += taxAmount;
      });
    });

    const taxBreakupArray = Object.values(taxMap);
    const taxSum = taxBreakupArray.reduce((sum, tax) => sum + toNumber(tax.amount), 0);

    let delivery = 0;
    const deliverySettings = settings?.delivery_settings || {};
    if (
      deliverySettings.enable_delivery_charges &&
      !staticRateCardSources.includes(orderSource.value) &&
      sub < toNumber(deliverySettings.free_above_order_value)
    ) {
      const sourceCharge = Array.isArray(deliverySettings.rate_card_specific_charges)
        ? deliverySettings.rate_card_specific_charges.find((c) => c.source === orderSource.value)
        : null;
      delivery = toNumber(sourceCharge?.charge);
      if (
        !sourceCharge &&
        deliverySettings.calculation_mode === "flat" &&
        Array.isArray(deliverySettings.distance_based_charges) &&
        deliverySettings.distance_based_charges.length
      ) {
        delivery = toNumber(deliverySettings.distance_based_charges[0]?.charge);
      }
    }

    const billingSettings = settings?.billing_settings || {};
    const service = billingSettings.enable_service_charges
      ? sub * (toNumber(billingSettings.service_charge_percentage) / 100)
      : 0;

    let totalBeforeDiscount = sub + additionalTax + service;
    if (deliverySettings.include_delivery_in_bill_total) totalBeforeDiscount += delivery;

    const roundedBeforeDiscount =
      billingSettings.round_off_total_amount === "nearest_1"
        ? Math.round(totalBeforeDiscount)
        : totalBeforeDiscount;

    const discount   = roundedBeforeDiscount * (toNumber(discountPercentage) / 100);
    const finalTotal = isComplementary ? 0 : Math.max(0, roundedBeforeDiscount - discount);

    return {
      subtotal: sub, taxTotal: taxSum, taxBreakup: taxBreakupArray,
      deliveryCharge: delivery, serviceCharge: service, grandTotal: finalTotal,
      unroundedTotal: isComplementary ? 0 : Math.max(0, totalBeforeDiscount - discount),
      discountAmount: discount,
    };
  }, [billItems, isComplementary, orderSource.value, settings, discountPercentage]);

  const itemCount     = billItems.length;
  const totalQty      = billItems.reduce((sum, item) => sum + toNumber(item.qty), 0);
  const paidAmount    = partialPayments.reduce((sum, p) => sum + toNumber(p.amount), 0);
  const hasDuePayment = partialPayments.some((p) => String(p.method).toLowerCase() === "due");
  const balanceAmount = Math.max(0, grandTotal - paidAmount);

  const generateBillNumber = () => {
    const formatting = settings?.billing_settings?.bill_no_formatting;
    if (!formatting) return `CSM-${Date.now().toString().slice(-4)}`;
    const { prefix, yearFormat, startNumber, yearPosition, numberPadding } = formatting;
    const year   = yearFormat === "financial" ? moment().subtract(3, "months").year() : moment().year();
    const number = (toNumber(startNumber) + Date.now()) % 100000;
    const paddedNumber = String(number).padStart(toNumber(numberPadding, 5), "0");
    return yearPosition === "suffix" ? `${prefix}-${paddedNumber}-${year}` : `${prefix}-${year}-${paddedNumber}`;
  };

  const resetBill = useCallback(() => {
    const defaultPayment = getDefaultPaymentOption();
    setBillItems([]);
    setCustomerName("");
    setCustomerPhone("");
    setPaymentMode(defaultPayment);
    setOrderSource({ value: "Dine-In", label: "Dine-In" });
    setRateCard(null);
    setDiscountPercentage(0);
    setPartialPayments([]);
    setIsPartialPaymentEnabled(false);
    setCurrentPartialMethod(defaultPayment?.value || "CASH");
    setCurrentPartialAmount("");
    setIsComplementary(false);
    setIsFooterExpanded(false);
    setSearchTerm("");
    debouncedSetSearchTerm.cancel?.();
  }, [debouncedSetSearchTerm, getDefaultPaymentOption]);

  const handleAddPartialPayment = () => {
    if (!settings?.payment_settings?.enable_multiple_payments) {
      notify("error","Multiple payments are not enabled.");
      return;
    }
    const newAmount = toNumber(currentPartialAmount);
    if (newAmount <= 0) { notify("error","Please enter a valid amount."); return; }
    if (paidAmount + newAmount > grandTotal + 0.009) {
      notify("error",`Amount exceeds total bill (${currency}${money(grandTotal)}).`);
      return;
    }
    const existingPayment = partialPayments.find(
      (p) => p.method === currentPartialMethod && toNumber(p.amount) === newAmount
    );
    if (existingPayment) { notify("error","This payment already exists."); return; }
    setPartialPayments((prev) => [...prev, { method: currentPartialMethod, amount: newAmount }]);
    setCurrentPartialAmount("");
  };

  const handleRemovePartialPayment = (index) => {
    setPartialPayments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleGenerateBill = async () => {
    if (isSaving) return;
    if (!billItems.length) { notify("error","Please add items to the bill."); return; }
    if (!isComplementary && !isPartialPaymentEnabled && !paymentMode) {
      notify("error","Please select a payment mode or enable partial payment.");
      return;
    }
    if (isPartialPaymentEnabled) {
      if (!partialPayments.length) { notify("error","Please add at least one partial payment."); return; }
      if (paidAmount < grandTotal - 0.009 && !hasDuePayment) {
        notify("error","Please cover the full amount or mark the remaining as Due.");
        return;
      }
      if (paidAmount > grandTotal + 0.009) {
        notify("error","Partial payments cannot exceed the bill total.");
        return;
      }
    }

    const cashPaidAmount = isPartialPaymentEnabled
      ? partialPayments.filter((p) => String(p.method).toLowerCase() !== "due").reduce((s, p) => s + toNumber(p.amount), 0)
      : grandTotal;
    const dueAmount = isPartialPaymentEnabled
      ? partialPayments.filter((p) => String(p.method).toLowerCase() === "due").reduce((s, p) => s + toNumber(p.amount), 0)
      : 0;

    const order = {
      order_number: generateBillNumber(),
      customer_name: customerName || null,
      customer_phone: customerPhone || null,
      order_type: "Counter",
      source: orderSource.value,
      status: "Completed",
      is_settled: isComplementary || (!isPartialPaymentEnabled && Boolean(paymentMode)) || (cashPaidAmount >= grandTotal && !hasDuePayment),
      billing_type: "Direct",
      subtotal,
      tax_amount: taxTotal,
      tax_breakup: taxBreakup.map((tax) => ({ tax_id: tax.tax_id, tax_name: tax.tax_name, tax_rate: tax.tax_rate, amount: tax.amount })),
      business_date: getBusinessDate(),
      items: billItems.map((item) => ({
        menu_id: item.menu_id, name: item.name, qty: item.qty,
        price_standard: item.price, amount: item.amount,
        is_taxable: item.is_taxable, tax_id: item.tax_id,
        tax_name: item.tax_name, tax_rate: item.total_tax_rate,
        tax_breakdown: item.tax_breakdown, is_price_exclusive_tax: item.is_price_exclusive_tax,
      })),
      payment_method: isComplementary ? "Complementary" : isPartialPaymentEnabled ? "Partial" : paymentMode?.value,
      discount_percentage: toNumber(discountPercentage),
      discount_amount: discountAmount,
      partial_payments: isPartialPaymentEnabled ? partialPayments : [],
      is_complementary: isComplementary,
      delivery_charge: deliveryCharge,
      service_charge: serviceCharge,
      grand_total: grandTotal,
      paid_amount: isComplementary ? 0 : cashPaidAmount,
      due_amount: dueAmount,
    };

    setIsSaving(true);

    try {
      const response = await axios.post(`${apiUrl}/pos/orders/orders`, order, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.data || !response.data.order_id) {
        throw new Error("Invalid response from server: Order ID not found");
      }

      // Browser print removed — kitchen agent prints via Socket.IO.
      // createOrder backend handler emits bill:print automatically after saving.
     notify("success",
        isComplementary
          ? "Complimentary bill saved. Slip sent to kitchen printer."
          : "Bill saved. Printing to kitchen printer..."
      );
      resetBill();

    } catch (err) {
      console.error("Error saving order:", err);
      notify("error",err.response?.data?.error || "Failed to save order");
    } finally {
      setIsSaving(false);
    }
  };

  const hasBlockingError = error && menuItems.length === 0 && !bootLoading;

  if (hasBlockingError) {
    return (
      <div className="ns-counter-error">
        <style>{counterStyles}</style>
        <div className="ns-counter-error-card">
          <AlertCircle size={34} />
          <h3>Unable to load counter billing</h3>
          <p>{error}</p>
          <button type="button" onClick={() => window.location.reload()}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="ns-counter-root">
      <style>{counterStyles}</style>

      <main className="ns-counter-workspace">
        <section className="ns-counter-panel ns-counter-menu-panel">
          <div className="ns-counter-menu-tools">
            {bootLoading && !categories.length ? (
              <div className="ns-counter-inline-loader">
                <Spin size="small" />
                <span>Loading categories...</span>
              </div>
            ) : categories.length > 0 ? (
              <CategoryTabs
                categories={categories}
                selectedCategory={selectedCategory}
                setSelectedCategory={setSelectedCategory}
              />
            ) : (
              <div className="ns-counter-empty-strip">No active categories configured.</div>
            )}
            <SearchBar
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              debouncedSetSearchTerm={debouncedSetSearchTerm}
              filteredItems={displayedItems}
              handleAddItem={handleAddItem}
              highlightedIndex={highlightedIndex}
              setHighlightedIndex={setHighlightedIndex}
            />
          </div>

          <div className="ns-counter-menu-grid-area">
            {bootLoading && menuItems.length === 0 ? (
              <div className="ns-counter-menu-loader">
                <Spin size="large" />
                <strong>Loading menu items...</strong>
                <span>Counter billing will be ready in a moment.</span>
              </div>
            ) : (
              <MenuItemGrid
                filteredItems={filteredItems}
                billItems={billItems}
                handleAddItem={handleAddItem}
                settings={settings}
                apiUrl={apiUrl}
                token={token}
              />
            )}
          </div>
        </section>

        <aside
          className={`ns-counter-panel ns-counter-bill-panel ${bootLoading ? "is-booting" : ""}`}
          aria-busy={bootLoading}
        >
          {bootLoading ? (
            <div className="ns-counter-bill-boot-overlay" aria-live="polite">
              <Spin />
              <strong>Preparing counter…</strong>
              <span>Loading payment options and settings</span>
            </div>
          ) : null}

          <div className="ns-counter-bill-head">
            <div className="ns-counter-bill-title">
              <span className="ns-counter-bill-icon"><CreditCard size={18} /></span>
              <div>
                <h3>Counter Billing</h3>
                <p>
                  {itemCount} item type{itemCount === 1 ? "" : "s"} · {Number(totalQty).toFixed(totalQty % 1 ? 2 : 0)} qty
                </p>
              </div>
            </div>

            <div className="ns-counter-bill-actions">
              <Select
                options={sourceOptions}
                value={orderSource}
                onChange={setOrderSource}
                placeholder="Source"
                className="ns-counter-source-select"
                classNamePrefix="ns-counter-source"
                styles={counterSelectStyles}
                menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                menuPosition="fixed"
                menuPlacement="auto"
                menuShouldScrollIntoView={false}
                isDisabled={bootLoading}
              />
              <button
                type="button"
                className="ns-counter-reset-btn"
                onClick={resetBill}
                disabled={bootLoading || (!billItems.length && !customerName && !customerPhone)}
              >
                <RotateCcw size={15} />
                New Bill
              </button>
            </div>
          </div>

          <div className="ns-counter-customer-card">
            <label>
              <span><User size={14} />Customer Name</span>
              <Input value={customerName} onChange={(e) => setCustomerName(e.target.value)} placeholder="Optional" allowClear disabled={bootLoading} />
            </label>
            <label>
              <span><Phone size={14} />Phone</span>
              <Input value={customerPhone} onChange={(e) => setCustomerPhone(e.target.value)} placeholder="Optional" allowClear disabled={bootLoading} />
            </label>
          </div>

          <div className="ns-counter-bill-scroll">
            <BillItemList
              billItems={billItems}
              handleQuantityChange={handleQuantityChange}
              handleRemoveItem={handleRemoveItem}
              rateCard={rateCard}
              itemCount={itemCount}
              orderSource={orderSource}
              setOrderSource={setOrderSource}
              sourceOptions={sourceOptions}
            />
            <BillSummary
              subtotal={subtotal}
              taxTotal={taxTotal}
              taxBreakup={taxBreakup}
              deliveryCharge={deliveryCharge}
              serviceCharge={serviceCharge}
              grandTotal={grandTotal}
              unroundedTotal={unroundedTotal}
              discountPercentage={discountPercentage}
              setDiscountPercentage={setDiscountPercentage}
              taxIncluded={taxIncluded}
              setTaxIncluded={setTaxIncluded}
              isComplementary={isComplementary}
              setIsComplementary={setIsComplementary}
              isPartialPaymentEnabled={isPartialPaymentEnabled}
              setIsPartialPaymentEnabled={setIsPartialPaymentEnabled}
              partialPayments={partialPayments}
              setPartialPayments={setPartialPayments}
              currentPartialMethod={currentPartialMethod}
              setCurrentPartialMethod={setCurrentPartialMethod}
              currentPartialAmount={currentPartialAmount}
              setCurrentPartialAmount={setCurrentPartialAmount}
              handleAddPartialPayment={handleAddPartialPayment}
              handleRemovePartialPayment={handleRemovePartialPayment}
              paymentMode={paymentMode}
              setPaymentMode={setPaymentMode}
              paymentOptions={paymentOptions}
              isFooterExpanded={isFooterExpanded}
              setIsFooterExpanded={setIsFooterExpanded}
              handleGenerateBill={handleGenerateBill}
              billItems={billItems}
              isSaving={isSaving}
            />
          </div>

          <div className="ns-counter-total-strip">
            <div className="ns-counter-total-left">
              <span>Grand Total</span>
              <strong>{currency}{money(grandTotal)}</strong>
              {isPartialPaymentEnabled ? (
                <small>Balance: {currency}{money(balanceAmount)}</small>
              ) : null}
            </div>
            <button
              type="button"
              className="ns-counter-generate-btn"
              onClick={handleGenerateBill}
              disabled={isSaving || bootLoading || !billItems.length}
            >
              <CreditCard size={17} />
              {isSaving ? "Saving..." : "Generate Bill"}
            </button>
          </div>
        </aside>
      </main>

      {isSaving ? (
        <div className="ns-counter-saving">
          <div className="ns-counter-saving-card">
            <Spin />
            <span>Saving counter bill...</span>
          </div>
        </div>
      ) : null}

      <button
        type="button"
        className="ns-counter-floating-refresh"
        onClick={() => window.location.reload()}
        title="Reload counter billing"
      >
        <RefreshCw size={17} />
      </button>

      {/* Print iframe removed — printing handled by kitchen agent via Socket.IO */}
    </div>
  );
};

// NOTE: counterStyles CSS is preserved exactly from the original file.
// The document was truncated in this conversation — copy counterStyles verbatim
// from your original CounterBillingPage.jsx (everything after "const counterStyles = `")
// No CSS changes are needed — only the JS above was modified.
const counterStyles = `
  .ns-counter-root {
    position: relative;
    min-height: 100%;
    background:
      radial-gradient(circle at 8% 0%, rgba(37, 99, 235, 0.05), transparent 30%),
      #f8fafc;
    padding: 8px min(468px, 37vw) 8px 8px;
    color: #0f172a;
  }

  .ns-counter-title-wrap,
  .ns-counter-bill-title {
    min-width: 0;
    display: flex;
    align-items: flex-start;
    gap: 12px;
  }

  .ns-counter-bill-icon {
    width: 40px;
    height: 40px;
    border-radius: 14px;
    background: #16a34a;
    color: #ffffff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 14px 28px rgba(22, 163, 74, 0.18);
    flex: 0 0 auto;
  }

  .ns-counter-bill-head h3 {
    color: #001f5b;
    font-size: 1.16rem;
    line-height: 1.15;
    font-weight: 950;
    margin: 0;
  }

  .ns-counter-bill-head p,
  .ns-counter-panel-head p {
    color: #64748b;
    font-size: 0.78rem;
    line-height: 1.45;
    font-weight: 760;
    margin: 4px 0 0;
  }

  .ns-counter-workspace {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: 8px;
    align-items: start;
    min-height: calc(100dvh - 16px);
  }

  .ns-counter-panel {
    min-width: 0;
    background: rgba(255, 255, 255, 0.96);
    border: 1px solid rgba(15, 23, 42, 0.08);
    border-radius: 22px;
    box-shadow: 0 14px 38px rgba(15, 23, 42, 0.075);
    overflow: hidden;
  }

  .ns-counter-menu-panel {
    min-height: calc(100dvh - 16px);
    display: flex;
    flex-direction: column;
    /*
      IMPORTANT: do NOT set overflow:hidden on this panel because
      child dropdowns (search suggestions, react-select menus) are
      portaled out, but other absolutely-positioned children might
      still need to be visible. We keep overflow on the inner grid
      area instead.
    */
    overflow: visible;
  }

  .ns-counter-bill-panel {
    /*
      Fix: sit BELOW the fixed top nav and ABOVE every other surface
      so the panel is never covered. The TOP_NAV_HEIGHT constant in
      the component drives the inline 'top' override via the parent
      padding, but here we hard-code 64px to match the ~60px nav + 4px gap.
    */
    position: fixed;
    top: 0px;
    right: 6px;
    bottom: 6px;
    width: min(452px, 36vw);
    z-index: 1100; /* well above the typical sticky nav (~1000), below toasts */
    max-height: none;
    min-height: 0;
    border-top: 4px solid #22c55e;
    display: flex;
    flex-direction: column;
    overflow: hidden; /* internal scroll handled by .ns-counter-bill-scroll */
  }

  .ns-counter-bill-panel.is-booting {
    pointer-events: none;
  }

  .ns-counter-bill-panel.is-booting > *:not(.ns-counter-bill-boot-overlay) {
    opacity: 0.45;
    filter: saturate(0.7);
  }

  .ns-counter-bill-boot-overlay {
    position: absolute;
    inset: 0;
    background: rgba(255, 255, 255, 0.72);
    backdrop-filter: blur(2px);
    -webkit-backdrop-filter: blur(2px);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    z-index: 5;
    pointer-events: auto;
    text-align: center;
    padding: 16px;
  }

  .ns-counter-bill-boot-overlay strong {
    color: #0f172a;
    font-weight: 950;
    font-size: 0.92rem;
    margin-top: 6px;
  }

  .ns-counter-bill-boot-overlay span {
    color: #64748b;
    font-size: 0.75rem;
    font-weight: 800;
  }

  .ns-counter-bill-head {
    flex: 0 0 auto;
    padding: 12px;
    border-bottom: 1px solid rgba(15, 23, 42, 0.08);
    background:
      radial-gradient(circle at top right, rgba(37, 99, 235, 0.08), transparent 28%),
      #ffffff;
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: start;
    gap: 10px;
  }

  .ns-counter-reset-btn {
    min-height: 36px;
    border-radius: 13px;
    border: 1px solid rgba(15, 23, 42, 0.10);
    background: #f8fafc;
    color: #334155;
    display: inline-flex;
    align-items: center;
    gap: 7px;
    padding: 0 11px;
    font-size: 0.74rem;
    font-weight: 900;
    cursor: pointer;
    transition: all 0.16s ease;
    white-space: nowrap;
  }

  .ns-counter-reset-btn:hover:not(:disabled) {
    background: #eff6ff;
    color: #1d4ed8;
    transform: translateY(-1px);
  }

  .ns-counter-reset-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .ns-counter-menu-tools {
    flex: 0 0 auto;
    padding: 12px;
    background: #ffffff;
    border-bottom: 1px solid rgba(15, 23, 42, 0.07);
    /*
      Critical: do NOT set overflow:hidden here. SearchBar's dropdown
      is now portaled to <body>, but we still want this container to
      not introduce a stacking-context trap.
    */
    position: relative;
    z-index: 1;
  }

  .ns-counter-inline-loader,
  .ns-counter-empty-strip {
    min-height: 48px;
    border-radius: 16px;
    background: #f8fafc;
    color: #64748b;
    border: 1px dashed rgba(100, 116, 139, 0.24);
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    font-size: 0.82rem;
    font-weight: 800;
  }

  .ns-counter-menu-grid-area {
    flex: 1 1 auto;
    min-height: 0;
    overflow: auto;
    padding: 12px;
    background: #f8fafc;
  }

  .ns-counter-menu-loader {
    min-height: 360px;
    border-radius: 22px;
    background: #ffffff;
    border: 1px dashed rgba(100, 116, 139, 0.24);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    text-align: center;
    gap: 8px;
    color: #64748b;
  }

  .ns-counter-menu-loader strong {
    color: #0f172a;
    font-weight: 950;
  }

  .ns-counter-customer-card {
    flex: 0 0 auto;
    background: #f8fafc;
    border-bottom: 1px solid rgba(15, 23, 42, 0.08);
    padding: 11px 13px;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 9px;
  }

  .ns-counter-customer-card label {
    display: grid;
    gap: 5px;
    min-width: 0;
  }

  .ns-counter-customer-card label > span {
    color: #334155;
    font-size: 0.72rem;
    font-weight: 950;
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }

  .ns-counter-customer-card .ant-input-affix-wrapper,
  .ns-counter-customer-card .ant-input {
    border-radius: 13px !important;
    min-height: 40px;
    font-weight: 760;
  }

  .ns-counter-bill-scroll {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 12px;
    background: #f8fafc;
    scrollbar-width: thin;
    scrollbar-color: #93c5fd #e2e8f0;
  }

  .ns-counter-bill-scroll::-webkit-scrollbar,
  .ns-counter-menu-grid-area::-webkit-scrollbar {
    width: 8px;
  }

  .ns-counter-bill-scroll::-webkit-scrollbar-track,
  .ns-counter-menu-grid-area::-webkit-scrollbar-track {
    background: #e2e8f0;
    border-radius: 999px;
  }

  .ns-counter-bill-scroll::-webkit-scrollbar-thumb,
  .ns-counter-menu-grid-area::-webkit-scrollbar-thumb {
    background: #93c5fd;
    border-radius: 999px;
  }

  .ns-counter-total-strip {
    flex: 0 0 auto;
    background: #ffffff;
    border-top: 1px solid rgba(15, 23, 42, 0.08);
    padding: 10px 12px;
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: center;
    gap: 10px;
    box-shadow: 0 -14px 34px rgba(15, 23, 42, 0.08);
    position: relative;
    z-index: 5;
  }

  .ns-counter-total-strip span {
    display: block;
    color: #64748b;
    font-size: 0.72rem;
    font-weight: 900;
  }

  .ns-counter-total-strip strong {
    display: block;
    color: #16a34a;
    font-size: 1.15rem;
    line-height: 1.2;
    font-weight: 950;
  }

  .ns-counter-saving {
    position: fixed;
    inset: 0;
    z-index: 2147483200;
    background: rgba(248, 250, 252, 0.48);
    backdrop-filter: blur(5px);
    -webkit-backdrop-filter: blur(5px);
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .ns-counter-saving-card {
    background: #ffffff;
    border: 1px solid rgba(15, 23, 42, 0.08);
    border-radius: 22px;
    box-shadow: 0 24px 70px rgba(15, 23, 42, 0.16);
    padding: 24px 30px;
    display: inline-flex;
    align-items: center;
    gap: 12px;
    color: #64748b;
    font-weight: 900;
  }

  .ns-counter-floating-refresh {
    position: fixed;
    right: 18px;
    bottom: 18px;
    width: 44px;
    height: 44px;
    border-radius: 16px;
    border: 0;
    background: #0f172a;
    color: #ffffff;
    box-shadow: 0 18px 38px rgba(15, 23, 42, 0.26);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    z-index: 20;
    cursor: pointer;
  }

  .ns-counter-error {
    min-height: 70vh;
    background: #f8fafc;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
  }

  .ns-counter-error-card {
    max-width: 460px;
    width: 100%;
    background: #ffffff;
    border: 1px solid rgba(220, 38, 38, 0.16);
    border-radius: 24px;
    box-shadow: 0 24px 70px rgba(15, 23, 42, 0.12);
    padding: 26px;
    text-align: center;
    color: #64748b;
  }

  .ns-counter-error-card svg {
    color: #dc2626;
    margin-bottom: 10px;
  }

  .ns-counter-error-card h3 {
    color: #0f172a;
    font-weight: 950;
    margin-bottom: 6px;
  }

  .ns-counter-error-card button {
    border: 0;
    border-radius: 14px;
    min-height: 40px;
    padding: 0 16px;
    background: #1d4ed8;
    color: #ffffff;
    font-weight: 900;
    margin-top: 10px;
  }

  /* Child-component finishing */
  .ns-counter-root .pos-menu-container {
    height: 100%;
    min-height: 0;
  }

  .ns-counter-root .pos-menu-header {
    margin-bottom: 0 !important;
  }

  .ns-counter-root .pos-category-tabs {
    padding-top: 0 !important;
  }

  .ns-counter-root .ant-select-dropdown,
  .ns-counter-root .ant-picker-dropdown,
  .ns-counter-root [class*="-menu"] {
    z-index: 2147483001 !important;
  }

  .ns-counter-bill-actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    min-width: 0;
  }

  .ns-counter-source-select {
    width: 150px;
    min-width: 150px;
  }

  .ns-counter-bill-head h3 {
    max-width: 170px;
  }

  .ns-counter-total-left {
    min-width: 0;
    display: grid;
    gap: 1px;
  }

  .ns-counter-total-left small {
    color: #64748b;
    font-size: 0.72rem;
    font-weight: 850;
  }

  .ns-counter-generate-btn {
    min-height: 46px;
    border-radius: 16px;
    border: 0;
    background: linear-gradient(135deg, #16a34a 0%, #22c55e 100%);
    color: #ffffff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 0 18px;
    font-size: 0.9rem;
    font-weight: 950;
    box-shadow: 0 16px 34px rgba(22, 163, 74, 0.22);
    cursor: pointer;
    white-space: nowrap;
    transition: transform 0.16s ease, box-shadow 0.16s ease, opacity 0.16s ease;
  }

  .ns-counter-generate-btn:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 20px 42px rgba(22, 163, 74, 0.30);
  }

  .ns-counter-generate-btn:disabled {
    opacity: 0.55;
    cursor: not-allowed;
    box-shadow: none;
  }

  .ns-counter-root .pos-bill-header,
  .ns-counter-root .pos-source-select {
    display: none !important;
  }

  @media (max-width: 1280px) {
    .ns-counter-root {
      padding: 8px;
    }

    .ns-counter-workspace {
      grid-template-columns: 1fr;
    }

    .ns-counter-bill-panel {
      position: relative;
      top: auto;
      right: auto;
      bottom: auto;
      width: auto;
      max-height: none;
      min-height: 520px;
      z-index: auto;
    }
  }

  @media (max-width: 768px) {
    .ns-counter-root {
      padding: 8px;
    }

    .ns-counter-bill-head {
      flex-direction: column;
      align-items: stretch;
    }

    .ns-counter-customer-card {
      grid-template-columns: 1fr;
    }
  }
`;

export default CounterBillingPage;